import { useCallback, useRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { ACCEPTED_FILE_EXTENSIONS } from '@/features/documents/documentUtils'
import { useGuidedIngestion } from './GuidedIngestionProvider'

interface AttachFileTriggerProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'> {
  children: ReactNode
  /**
   * Optional aria-label override. The wrapping button accepts standard
   * native attributes — class, title, disabled — so each composer surface
   * can style the trigger however it likes.
   */
  ariaLabel?: string
  /** Test hook forwarded to the underlying button (E2E selectors). */
  'data-testid'?: string
}

/**
 * STEP 7.10 — A11y-friendly file picker button that hands the picked file
 * over to {@link GuidedIngestionProvider}.
 *
 * The hidden ``<input type="file">`` lives alongside the visible button so
 * the picker is opened *during the user gesture* — Safari/iOS reject calls
 * to ``HTMLInputElement.click()`` that happen outside of a trusted event,
 * which would silently break attach if we tried to share a single global
 * input from the provider.
 *
 * Cancelling the OS file dialog leaves no file selected and the input is
 * reset so the same file can be picked twice in a row.
 */
export function AttachFileTrigger({
  children,
  ariaLabel,
  className,
  title,
  disabled,
  type: _type,
  ...buttonProps
}: AttachFileTriggerProps) {
  // Mark the unused destructured prop so the noUnusedParameters rule stays
  // satisfied while still preventing callers from overriding type="button".
  void _type
  const inputRef = useRef<HTMLInputElement>(null)
  const { openWithFile, isOpen } = useGuidedIngestion()

  const handleClick = useCallback(() => {
    if (disabled || isOpen) return
    inputRef.current?.click()
  }, [disabled, isOpen])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      // Always reset the input so picking the same filename twice in a row
      // still fires a change event.
      event.target.value = ''
      if (!file) return
      openWithFile(file)
    },
    [openWithFile],
  )

  return (
    <>
      <button
        {...buttonProps}
        type="button"
        className={className}
        title={title}
        aria-label={ariaLabel}
        disabled={disabled || isOpen}
        onClick={handleClick}
      >
        {children}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_EXTENSIONS}
        className="sr-only-file"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleChange}
      />
    </>
  )
}
